import { z } from "zod";
import {
  Button,
  Grid,
  Header,
  Icon,
  Message,
  Modal,
  Segment,
} from "semantic-ui-react";
import { AxiosEmployeeAreaAPI } from "../../common/Api";
import { Form, FormSelect } from "../../common/components/Form";
import { Formik, useField } from "formik";
import React, { useState } from "react";

import { AxiosAreasAPI } from "../../areas/Api";
import { ChangeButton } from "../../common/components/ChangeButton";
import { ChangeDateInput } from "../../common/components/ChangeDateInput";
import { HeaderItem } from "../../common/components/HeaderItem";
import { Icons } from "../../common/Icons";
import ReactFocusLock from "react-focus-lock";
import { ReviewAccessInput } from "../../common/components/ReviewAccessInput";
import { ServerErrorMessage } from "../../common/components/ServerErrorMessage";
import { ToastId } from "../../common/ToastIds";
import { toast } from "react-toastify";
import { useAsync } from "react-async";
import { useDebouncedSearch } from "../../utils/Hooks";
import { useSettings } from "../../utils/Settings";

const AreaInfoPanel = ({ header = "Área", area }) => {
  return (
    <>
      <Header>{header}</Header>
      <HeaderItem icon={Icons.AREA} label="Nome" value={area?.name} />
      <HeaderItem icon={Icons.CODE} label="Código" value={area?.code} />
      <HeaderItem
        label="Área-pai"
        icon={Icons.PARENT_AREA}
        value={area?.parent_name}
      />
    </>
  );
};

const PreviewChangePanel = ({ currentArea, selectedArea }) => {
  return (
    <Grid stackable>
      <Grid.Column width={7}>
        <AreaInfoPanel header="Área atual" area={currentArea} />
      </Grid.Column>
      <Grid.Column width={2} verticalAlign={"middle"}>
        <Icon size="big" name={"arrow right"} />
      </Grid.Column>
      <Grid.Column width={7}>
        <AreaInfoPanel header="Nova área" area={selectedArea} />
      </Grid.Column>
    </Grid>
  );
};

const EmployeeAreaSelect = ({ onAreaChange, ...props }) => {
  const [, , helpers] = useField(props);

  const [, debouncedSearch, setSearch] = useDebouncedSearch();

  const {
    data: areas,
    isPending,
    error,
  } = useAsync({
    promiseFn: AxiosAreasAPI.getAll,
    search: debouncedSearch,
    watch: debouncedSearch,
  });

  const handleChange = (e, v) => {
    helpers.setValue(v.value);

    if (onAreaChange) {
      const selectedOption = options.find((e) => e.value === v.value);
      const area = selectedOption ? selectedOption.area : null;
      onAreaChange(area);
    }
  };

  const handleSearch = (e, { searchQuery }) => {
    setSearch(searchQuery);
  };

  let options = areas
    ? areas.results.map((a) => ({
        text: a.name,
        area: a,
        value: a.api_url,
        content: (
          <Header style={{ fontSize: "1rem" }}>
            {a.name}
            <Header.Subheader>
              {a.code} / {a.parent_name ? a.parent_name : "-"} / Data de
              criação:{" "}
              {a.current_record.timespan.lower.format("DD/MM/YYYY").toString()}
            </Header.Subheader>
          </Header>
        ),
      }))
    : [];

  return (
    <Grid.Row>
      <Grid.Column>
        {error && (
          <Message error visible>
            <Message.Content>
              Não foi possível carregar a lista de áreas, por favor tente
              novamente.
            </Message.Content>
          </Message>
        )}
        <FormSelect
          clearable
          scrolling
          loading={isPending}
          onSearchChange={handleSearch}
          onChange={handleChange}
          options={options}
          {...props}
        />
      </Grid.Column>
    </Grid.Row>
  );
};

const AreaChangeSchema = z.object({
  //TODO: Add appropriate employee type
  employee: z
    .object({})
    .passthrough()
    .refine((val) => val !== null && val !== undefined, {
      message: "Employee is required",
    }),
  new_area: z
    .string({
      required_error: "Por favor insira a área",
    })
    .min(1, "Por favor insira a área"),
  change_date: z.coerce.date({
    required_error: "Por favor insira a data",
    invalid_type_error: "Data inválida",
  }),
});

const EmployeeAreaChangeForm = ({
  employee,
  currentArea,
  onSuccess,
  onCancel,
}) => {
  const [selectedArea, setSelectedArea] = useState();

  const { run, isPending, isFulfilled, error } = useAsync({
    deferFn: AxiosEmployeeAreaAPI.changeArea,
  });

  const { data: dataSettings, isFetching: isAccessPending } = useSettings();

  if (isAccessPending) {
    return null;
  }

  const reviewAccessActive = dataSettings
    ? dataSettings.find((setting) => setting.key === "REVIEW_ACCESS_ACTIVE")
        .value === "True"
    : false;

  const formData = {
    employee: employee,
    new_area: "",
    change_date: "",
    review_access: reviewAccessActive,
  };

  const handleSubmit = (values, { setSubmitting }) => {
    run(values);
    setSubmitting(false);
  };

  if (isFulfilled) onSuccess();

  return (
    <>
      <Formik
        initialValues={formData}
        onSubmit={handleSubmit}
        validate={(values) => {
          const result = AreaChangeSchema.safeParse(values);
          if (!result.success) {
            return result.error.flatten().fieldErrors;
          }
        }}
      >
        <Form loading={isPending}>
          <Segment basic>
            <ServerErrorMessage error={error} />
            <Grid>
              <EmployeeAreaSelect
                name="new_area"
                label="Nova área"
                onAreaChange={setSelectedArea}
              />
              <ChangeDateInput />
              {reviewAccessActive && <ReviewAccessInput />}
            </Grid>
          </Segment>
          <Segment basic>
            <PreviewChangePanel
              currentArea={currentArea}
              selectedArea={selectedArea}
            />
          </Segment>
          <Segment basic>
            <Button type="submit" primary>
              Alterar
            </Button>
            <Button basic onClick={onCancel}>
              Cancelar
            </Button>
          </Segment>
        </Form>
      </Formik>
    </>
  );
};

export const EmployeeAreaChangeModal = ({
  employee,
  currentArea,
  reload,
  trigger,
}) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSuccess = () => {
    handleClose();
    reload();
    toast.success("Área alterada com sucesso!", ToastId.AREA_CHANGE);
  };

  return (
    <Modal
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onFocus={(e) => e.stopPropagation()}
      trigger={
        trigger || <ChangeButton disabled={!employee._actions.change_area} />
      }
      style={{ maxWidth: "800px" }}
    >
      <Modal.Header>Alterar área de {employee.name}</Modal.Header>
      <Modal.Content>
        <ReactFocusLock>
          <EmployeeAreaChangeForm
            employee={employee}
            currentArea={currentArea}
            onSuccess={handleSuccess}
            onCancel={handleClose}
          />
        </ReactFocusLock>
      </Modal.Content>
    </Modal>
  );
};
